import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Quadrant, Tooth, ToothSide } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { ProbingSite } from 'libs/perio/src/lib/common/models';
import { PocketDepthSymbolComponent } from '../pocket-depth-symbol/pocket-depth-symbol.component';

@Component({
  selector: 'lib-pocket-depth-table',
  standalone: true,
  imports: [CommonModule, PocketDepthSymbolComponent, TranslateModule],
  templateUrl: './pocket-depth-table.component.html',
  styleUrl: './pocket-depth-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PocketDepthTableComponent {
  protected Quadrant = Quadrant;

  public isQuadrantSelected = input<boolean>(false);
  public currentTeeth = input<Tooth[]>([]);
  public previousTeeth = input<Tooth[]>([]);
  public overlayPosition = input<Quadrant | null>(null);
  public toothSide = input<ToothSide | null>(null);

  protected areLabelsDisplayedOnRightQuadrant = computed(() => {
    return (
      this.overlayPosition() === Quadrant.TopRight ||
      this.overlayPosition() === Quadrant.BottomRight ||
      (this.isQuadrantSelected() && (this.overlayPosition() === Quadrant.TopLeft || this.overlayPosition() === Quadrant.BottomLeft))
    );
  });

  protected areLabelsDisplayedOnLeftQuadrant = computed(() => {
    return !this.isQuadrantSelected() && (this.overlayPosition() === Quadrant.TopLeft || this.overlayPosition() === Quadrant.BottomLeft);
  });

  protected getBorderClass = computed(() => {
    if (this.isQuadrantSelected()) {
      return this.areLabelsDisplayedOnRightQuadrant() ? 'border-l-4' : 'border-r-4';
    }
    return this.areLabelsDisplayedOnRightQuadrant() ? 'border-l-2' : 'border-r-2';
  });

  protected getPocketDepths(tooth: Tooth): ProbingSite[] {
    const pocketDepth = this.toothSide() === 'buccal' ? tooth.perio.buccal.pocketDepth : tooth.perio.lingual.pocketDepth;
    const bleeding = this.toothSide() === 'buccal' ? tooth.perio.buccal.bleedingOnProbing : tooth.perio.lingual.bleedingOnProbing;
    const suppuration = this.toothSide() === 'buccal' ? tooth.perio.buccal.suppuration : tooth.perio.lingual.suppuration;

    switch (this.overlayPosition()) {
      case Quadrant.TopRight:
      case Quadrant.BottomRight: {
        return [
          {
            pocketDepth: pocketDepth.distal,
            bleeding: bleeding.distal,
            suppuration: suppuration.distal,
          },
          {
            pocketDepth: pocketDepth.central,
            bleeding: bleeding.central,
            suppuration: suppuration.central,
          },
          {
            pocketDepth: pocketDepth.mesial,
            bleeding: bleeding.mesial,
            suppuration: suppuration.mesial,
          },
        ];
      }
      case Quadrant.TopLeft:
      case Quadrant.BottomLeft: {
        return [
          {
            pocketDepth: pocketDepth.mesial,
            bleeding: bleeding.mesial,
            suppuration: suppuration.mesial,
          },
          {
            pocketDepth: pocketDepth.central,
            bleeding: bleeding.central,
            suppuration: suppuration.central,
          },
          {
            pocketDepth: pocketDepth.distal,
            bleeding: bleeding.distal,
            suppuration: suppuration.distal,
          },
        ];
      }

      default: {
        return [];
      }
    }
  }

  protected pocketDepthSymbolPosition(): string {
    const position = this.overlayPosition();
    const side = this.toothSide();

    if (position?.includes('top')) {
      return side === 'buccal' ? 'items-end' : 'items-start';
    }

    if (position?.includes('bottom')) {
      return side === 'lingual' ? 'items-end' : 'items-start';
    }

    return '';
  }

  protected toothSideLabel(): string {
    return this.toothSide() === 'buccal' ? 'perio_common_label_facial' : 'perio_common_label_lingual';
  }
}
